const PDFJS_VER = '2.16.105';
const PDFJS_BASE = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VER}`;

let pdfJsLoadPromise = null;
let kioskRenderSeq = 0;

export function ensurePdfJs() {
  if (pdfJsLoadPromise) return pdfJsLoadPromise;
  pdfJsLoadPromise = new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker.min.js`;
      resolve(window.pdfjsLib);
      return;
    }
    const s = document.createElement('script');
    s.src = `${PDFJS_BASE}/pdf.min.js`;
    s.async = true;
    s.onload = () => {
      const lib = window.pdfjsLib;
      if (!lib) {
        reject(new Error('pdf.js missing'));
        return;
      }
      lib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker.min.js`;
      resolve(lib);
    };
    s.onerror = () => reject(new Error('pdf.js load failed'));
    document.head.appendChild(s);
  });
  return pdfJsLoadPromise;
}

export function stripPdfUrlHash(url) {
  if (!url) return '';
  const s = String(url);
  const i = s.indexOf('#');
  return i >= 0 ? s.slice(0, i) : s;
}

const API_V1_PREFIX = '/api/v1';

const KIOSK_MAX_CANVAS_WIDTH = 2048;

export function resolveKioskPdfJsUrl(originalUrl) {
  const base = stripPdfUrlHash(originalUrl);
  if (!base || base.startsWith('blob:')) return base;
  try {
    const u = new URL(base, window.location.href);
    const slidePath = u.pathname.replace(/^\/+/, '');
    if (slidePath.includes('..')) return base;
    if (!/^slide\//i.test(slidePath)) return base;
    if (!/\.pdf$/i.test(slidePath.split('/').pop() || '')) return base;
    if (u.origin === window.location.origin) return base;
    return `${API_V1_PREFIX}/touch_screen_modules/slide_pdf_proxy?path=${encodeURIComponent(slidePath)}`;
  } catch (e) {
    return base;
  }
}

export function cancelKioskPdfRender() {
  kioskRenderSeq += 1;
}

function kioskPdfHostInnerWidth(hostEl) {
  return Math.max(1, Math.floor(hostEl.clientWidth || hostEl.parentElement?.clientWidth || 1));
}

export function renderKioskTapPdfPages(hostEl, pdfUrl, { onLayout } = {}) {
  const mySeq = (kioskRenderSeq += 1);
  const docUrl = resolveKioskPdfJsUrl(pdfUrl);
  if (!hostEl || !docUrl) {
    return Promise.resolve();
  }

  hostEl.innerHTML = '<p class="small text-muted p-3 mb-0">Loading screens…</p>';

  return ensurePdfJs()
    .then((pdfjsLib) => {
      if (mySeq !== kioskRenderSeq) return null;
      return pdfjsLib.getDocument({ url: docUrl }).promise;
    })
    .then((pdf) => {
      if (!pdf || mySeq !== kioskRenderSeq) return;
      hostEl.innerHTML = '';
      const iw = kioskPdfHostInnerWidth(hostEl);
      const num = pdf.numPages;

      const run = (pageNum) => {
        if (mySeq !== kioskRenderSeq) return;
        if (pageNum > num) {
          if (typeof onLayout === 'function') onLayout();
          return;
        }
        pdf.getPage(pageNum).then((page) => {
          if (mySeq !== kioskRenderSeq) return;
          const vp1 = page.getViewport({ scale: 1 });
          const fitScale = iw / vp1.width;
          const scale = Math.min(fitScale, KIOSK_MAX_CANVAS_WIDTH / vp1.width);
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = Math.max(1, Math.floor(viewport.width));
          canvas.height = Math.max(1, Math.floor(viewport.height));
          canvas.style.display = 'block';
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          canvas.style.verticalAlign = 'top';
          canvas.style.background = '#fff';
          page.render({ canvasContext: ctx, viewport }).promise.then(() => {
            if (mySeq !== kioskRenderSeq) return;
            hostEl.appendChild(canvas);
            if (pageNum === 1 && typeof onLayout === 'function') onLayout();
            run(pageNum + 1);
          });
        });
      };
      run(1);
    })
    .catch((err) => {
      if (mySeq !== kioskRenderSeq) return;
      console.error('Tap PDF (kiosk) render failed:', err);
      hostEl.innerHTML =
        '<p class="small text-danger p-3 mb-0">Could not display this PDF. The file may block access from this site (CORS), or the network failed.</p>';
    });
}
